import { Injectable, inject } from '@angular/core';
import { Observable, map, shareReplay, tap } from 'rxjs';

import { CiService } from '../../../_services/ci.service';
import {
  CiQuestion,
  CiQuestionApiOption,
  CiQuestionApiResponse,
} from '../models/ci-question.model';

/**
 * Reads the adjustment matrix from the CI questions API.
 *
 * The API stores weights as Mongo decimals, which serialise as strings often
 * enough that coercing here is cheaper than debugging a silent NaN in the
 * benchmark later.
 */
@Injectable({ providedIn: 'root' })
export class CiQuestionApiService {
  private readonly ci = inject(CiService);

  /** Cached for the session — the matrix does not change mid-analysis. */
  private questions$?: Observable<CiQuestion[]>;

  getQuestions(): Observable<CiQuestion[]> {
    this.questions$ ??= this.ci
      .getCIQns()
      .pipe(
        map((response) => response.map((item) => this.toQuestion(item))),
        // Never keep an empty matrix for the session — the next visit should retry.
        tap((questions) => {
          if (!questions.length) this.questions$ = undefined;
        }),
        shareReplay({ bufferSize: 1, refCount: false }),
      );
    return this.questions$;
  }

  /** Drops the cache so the next read hits the API. */
  invalidate(): void {
    this.questions$ = undefined;
  }

  private toQuestion(item: CiQuestionApiResponse): CiQuestion {
    return {
      key: this.toKey(item.question),
      label: item.question,
      // Every option on a question carries the same reason; take the first.
      whyItMatters: item.options?.[0]?.whyItMatters ?? '',
      options: (item.options ?? []).map((option) => this.toOption(option)),
    };
  }

  private toOption(option: CiQuestionApiOption): CiQuestionOptionInternal {
    return {
      value: this.toKey(option.option),
      label: option.option,
      weight: Number(option.weight) || 0,
    };
  }

  /** "Legal Issue / OC Status" -> "legal-issue-oc-status" */
  private toKey(text: string): string {
    return text
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }
}

type CiQuestionOptionInternal = CiQuestion['options'][number];
